<template>
	<v-card>
		<v-card-title>
			Painel de integração Itaú
			<v-spacer />
			<v-btn small outlined color="primary" :loading="carregandoStatus" @click="carregarTudo">
				<v-icon left small>mdi-refresh</v-icon>Atualizar
			</v-btn>
		</v-card-title>
		<v-divider />
		<v-tabs v-model="aba" show-arrows>
			<v-tab>Visão geral</v-tab>
			<v-tab>Cadastro no Itaú</v-tab>
			<v-tab>Baixa por filial</v-tab>
			<v-tab>Atividade</v-tab>
			<v-tab>Testes</v-tab>
		</v-tabs>
		<v-divider />

		<v-tabs-items v-model="aba">
			<!-- Visão geral -->
			<v-tab-item>
				<v-card-text>
					<div class="text-subtitle-1 font-weight-bold mb-2">Configuração do webhook de boletos</div>
					<div v-if="status">
						<v-chip small class="mr-2 mb-2" :color="status.tabeladisponivel ? 'green' : 'red'" text-color="white">
							Tabela de chaves {{ status.tabeladisponivel ? "disponível" : "não criada" }}
						</v-chip>
						<v-chip v-if="status.tabeladisponivel" small class="mr-2 mb-2" :color="status.filiaiscomchaves ? 'green' : 'grey'" text-color="white">
							{{ status.filiaiscomchaves }} filial(is) com chaves
						</v-chip>
						<v-chip v-if="status.tabeladisponivel" small class="mr-2 mb-2" :color="status.filiaissincronizadas ? 'green' : 'grey'" text-color="white">
							{{ status.filiaissincronizadas }} cadastrada(s) no Itaú
						</v-chip>
						<v-chip small class="mr-2 mb-2" :color="status.tokentabeladisponivel ? 'green' : 'orange'" text-color="white">
							{{ status.tokentabeladisponivel ? `Tokens guardados no banco: ${status.tokensvigentes} vigente(s)` : "Tokens só na memória (tabela de tokens não criada)" }}
						</v-chip>
						<v-chip small class="mr-2 mb-2" :color="status.urlwebhook ? 'green' : 'orange'" text-color="white">
							URL do webhook {{ status.urlwebhook ? "definida" : "não definida" }}
						</v-chip>
						<v-chip small class="mr-2 mb-2" :color="status.urloauth ? 'green' : 'orange'" text-color="white">
							URL do token {{ status.urloauth ? "definida" : "não definida" }}
						</v-chip>
						<v-chip small class="mr-2 mb-2" :color="status.confiarproxy ? 'blue' : 'grey'" text-color="white">
							Proxy confiável {{ status.confiarproxy ? "ligado" : "desligado" }}
						</v-chip>
						<v-chip small class="mr-2 mb-2" :color="status.configdisponivel ? 'green' : 'red'" text-color="white">
							Configuração {{ status.configdisponivel ? "disponível" : "não criada" }}
						</v-chip>
						<v-chip small class="mr-2 mb-2" :color="status.baixaautomatica ? 'green' : 'orange'" text-color="white" v-if="status.configdisponivel">
							Baixa automática {{ status.baixaautomatica ? "ligada" : "desligada (somente registrar)" }}
						</v-chip>
						<v-chip small class="mr-2 mb-2" :color="status.acessosrecusados24h >= 10 ? 'red' : status.acessosrecusados24h > 0 ? 'orange' : 'green'" text-color="white" v-if="status.configdisponivel">
							{{ status.acessosrecusados24h }} acesso(s) recusado(s) nas últimas 24h
						</v-chip>
						<v-chip small class="mr-2 mb-2" color="grey" text-color="white" v-if="status.configdisponivel">
							{{ status.ipsconhecidos }} IP(s) conhecido(s) do Itaú
						</v-chip>
						<v-chip small class="mr-2 mb-2" color="blue-grey" text-color="white" v-if="status.configdisponivel">
							Último aviso do Itaú: {{ status.ultimoaviso || "nenhum nos últimos 30 dias" }}
						</v-chip>
						<div v-if="status.configdisponivel" class="mt-3">
							<div class="text-subtitle-2 mb-2">Endereços informados ao Itaú no cadastro</div>
							<v-row dense>
								<v-col cols="12" md="6">
									<v-text-field v-model="config.urlwebhook" label="URL de notificação" outlined dense hide-details :readonly="!podeGerenciar" />
								</v-col>
								<v-col cols="12" md="6">
									<v-text-field v-model="config.urloauth" label="URL do token (OAuth)" outlined dense hide-details :readonly="!podeGerenciar" />
								</v-col>
								<v-col cols="12" md="4">
									<v-text-field v-model="config.escopo" label="Escopo" outlined dense hide-details :readonly="!podeGerenciar" />
								</v-col>
								<v-col cols="12" md="4">
									<v-text-field v-model.number="config.valorminimo" label="Valor mínimo (R$)" type="number" step="0.01" outlined dense hide-details :readonly="!podeGerenciar" />
								</v-col>
								<v-col cols="12">
									<v-text-field
										v-model="config.alertaemails"
										label="E-mails que recebem os alertas de segurança do webhook"
										hint="Separe por vírgula. Vazio: os alertas aparecem só no painel, sem e-mail."
										persistent-hint
										prepend-inner-icon="mdi-email-alert-outline"
										outlined
										dense
										:readonly="!podeGerenciar"
									/>
									<v-btn v-if="podeGerenciar" small outlined color="primary" class="mt-3" :loading="testandoEmail" :disabled="!status.alertaemails" @click="enviarEmailTeste">
										<v-icon left small>mdi-email-fast-outline</v-icon>Enviar e-mail de teste
									</v-btn>
									<span v-if="podeGerenciar && !status.alertaemails" class="text-caption grey--text ml-2">Salve os e-mails antes de testar.</span>
								</v-col>
								<v-col cols="12">
									<v-switch
										v-model="config.baixaautomatica"
										color="green"
										dense
										hide-details
										class="mt-0"
										:readonly="!podeGerenciar"
										:label="config.baixaautomatica ? 'Baixa automática ligada: ao receber o pagamento, o título é baixado na contabilidade' : 'Baixa automática desligada: o webhook apenas registra as notificações, sem baixar nada'"
									/>
								</v-col>
								<v-col cols="12" md="4" v-if="podeGerenciar">
									<v-btn small color="primary" :loading="salvandoConfig" @click="salvarConfig">Salvar configuração</v-btn>
								</v-col>
							</v-row>
							<div class="text-caption grey--text mt-2">
								As URLs precisam ser https e ter nome de domínio (o Itaú não aceita IP). Alterar aqui não muda um cadastro que já existe no Itaú: use a atualização do cadastro depois.
							</div>
						</div>
						<div class="text-body-2 red--text mt-1" v-if="!status.tabeladisponivel || !status.configdisponivel">
							As tabelas do webhook ainda não existem. Rode o SQL boleto-webhook-credencial.sql no DB2; sem elas o endpoint de token recusa todas as chamadas do Itaú.
						</div>
					</div>
					<v-skeleton-loader v-else-if="carregandoStatus" type="chip" />

					<div class="text-subtitle-1 font-weight-bold mt-6 mb-2">Movimento dos últimos 7 dias</div>
					<div v-if="status && status.contagem7dias.length">
						<v-simple-table dense>
							<thead>
								<tr>
									<th>Tipo</th>
									<th>Quantidade</th>
									<th>Último</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="m in status.contagem7dias" :key="m.tipo">
									<td>{{ rotuloTipo(m.tipo) }}</td>
									<td>{{ m.total }}</td>
									<td>{{ formatarData(m.ultimo) }}</td>
								</tr>
							</tbody>
						</v-simple-table>
					</div>
					<div v-else-if="status" class="text-body-2 grey--text">
						Nenhum evento do webhook nos últimos 7 dias. Isso é esperado enquanto o Itaú não tiver o cadastro e nenhum boleto for pago.
					</div>
				</v-card-text>
			</v-tab-item>

			<!-- Cadastro no Itaú -->
			<v-tab-item>
				<v-card-text>
					<v-row dense align="center">
						<v-col cols="12" sm="4">
							<v-select
								v-model="filialCadastro"
								:items="opcoesFiliais"
								label="Filial"
								outlined
								dense
								hide-details
							/>
						</v-col>
						<v-col cols="12" sm="8">
							<v-btn
								class="mr-2"
								small
								outlined
								color="indigo"
								:disabled="!filialCadastro"
								:loading="consultandoCadastro"
								@click="consultarCadastro"
							>Consultar cadastro no Itaú</v-btn>
							<v-tooltip bottom :disabled="podeCadastrar">
								<template v-slot:activator="{ on, attrs }">
									<span v-bind="attrs" v-on="on">
										<v-btn
											v-if="podeGerenciar"
											small
											color="red darken-2"
											dark
											:disabled="!filialCadastro || !podeCadastrar"
											:loading="cadastrando"
											@click="dialogCadastrar = true"
										>Cadastrar webhook</v-btn>
									</span>
								</template>
								<span>Defina as variáveis do webhook no servidor antes (veja a Visão geral)</span>
							</v-tooltip>
						</v-col>
					</v-row>

					<div v-if="resultadoCadastro" class="mt-4">
						<v-chip small :color="corStatus(resultadoCadastro.statuscode)" text-color="white" class="mr-2">
							HTTP {{ resultadoCadastro.statuscode }}
						</v-chip>
						<span class="text-body-2">{{ explicarCadastro(resultadoCadastro.statuscode) }}</span>
						<pre class="bloco-codigo mt-3">{{ formatarResposta(resultadoCadastro.resposta) }}</pre>
					</div>
					<div class="text-caption grey--text mt-3">
						Consultar não altera nada no Itaú. Cadastrar cria o webhook de verdade, com as URLs e credenciais configuradas no servidor.
					</div>
				</v-card-text>
			</v-tab-item>

			<!-- Baixa por filial -->
			<v-tab-item>
				<v-card-text>
					<div class="text-subtitle-1 font-weight-bold mb-1">Baixa automática de boletos por filial</div>
					<div class="text-body-2 grey--text text--darken-1 mb-3">
						A baixa só acontece quando a chave geral (aba Visão geral) está ligada e a filial está ligada aqui. Filial desligada só registra o pagamento, que fica na Conciliação para baixa manual.
					</div>
					<v-alert v-if="status && status.configdisponivel && !status.baixaautomatica" dense outlined type="warning" class="mb-3">
						A chave geral está desligada: nenhuma filial baixa sozinha, mesmo ligada aqui.
					</v-alert>
					<v-alert v-if="!baixaFilial.disponivel && !carregandoBaixaFilial" dense outlined type="error" class="mb-3">
						A tabela BOLETOS.TB_WEBHOOK_BAIXA_FILIAL ainda não existe. Rode o SQL boleto-webhook-baixa-filial.sql no DB2.
					</v-alert>
					<v-simple-table dense>
						<thead>
							<tr>
								<th>Filial</th>
								<th>Conta do banco</th>
								<th>Baixa automática</th>
								<th>Alterado em</th>
								<th>Usuário</th>
								<th>Último token do Itaú</th>
								<th>Observação</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="f in linhasBaixaFilial" :key="f.idempresa">
								<td>{{ f.rotulo }}</td>
								<td>{{ f.conta || "-" }}</td>
								<td>
									<v-switch
										:key="`${f.idempresa}-${versaoSwitch}`"
										:input-value="f.ativa"
										color="green"
										dense
										hide-details
										class="mt-0 pt-0"
										:disabled="!podeGerenciar || !baixaFilial.disponivel || f.fora || (!f.conta && !f.ativa) || salvandoFilial === f.idempresa"
										:label="f.ativa ? 'Ligada' : 'Desligada'"
										@change="pedirAlterarBaixaFilial(f, $event)"
									/>
								</td>
								<td>{{ f.dtalteracao ? formatarData(f.dtalteracao) : "-" }}</td>
								<td>{{ f.idusuario || "-" }}</td>
								<td>{{ f.ultimotoken ? formatarData(f.ultimotoken) : "-" }}</td>
								<td>
									<v-chip v-if="f.fora" x-small color="grey" text-color="white">Forma 34 (depósito a identificar): fora da automação</v-chip>
									<v-chip v-else-if="!f.conta" x-small color="orange" text-color="white">Sem conta do banco em IVP.CONTA_BANCARIA_FILIAL</v-chip>
								</td>
							</tr>
							<tr v-if="!linhasBaixaFilial.length">
								<td colspan="7" class="grey--text">Nenhuma filial carregada.</td>
							</tr>
						</tbody>
					</v-simple-table>
					<div class="text-caption grey--text mt-3">
						Toda filial começa desligada. Cada alteração fica registrada na aba Atividade (tipo Configuração alterada). Recomenda-se ligar uma filial por vez e conferir as baixas no CISS antes de ligar a próxima.
					</div>
				</v-card-text>
			</v-tab-item>

			<!-- Atividade -->
			<v-tab-item>
				<v-card-text>
					<v-row dense align="center">
						<v-col cols="12" sm="3">
							<v-select v-model="grupoLog" :items="grupos" item-text="texto" item-value="valor" label="Tipo" outlined dense hide-details @change="carregarLog" />
						</v-col>
						<v-col cols="12" sm="3">
							<v-select v-model="filialLog" :items="opcoesFiliaisTodas" label="Filial" outlined dense hide-details @change="carregarLog" />
						</v-col>
						<v-col cols="12" sm="2">
							<v-select v-model="limiteLog" :items="[50, 100, 200, 500]" label="Linhas" outlined dense hide-details @change="carregarLog" />
						</v-col>
						<v-col cols="12" sm="4" class="d-flex align-center">
							<v-btn small outlined color="primary" class="mr-3" :loading="carregandoLog" @click="carregarLog">Atualizar</v-btn>
							<v-switch v-model="autoAtualizar" dense hide-details label="Atualizar a cada 15s" class="mt-0" @change="configurarAutoAtualizar" />
						</v-col>
					</v-row>

					<v-data-table
						class="mt-3"
						dense
						:headers="headersLog"
						:items="log"
						:items-per-page="25"
						no-data-text="Nenhum registro para esse filtro"
						:loading="carregandoLog"
						@click:row="abrirDetalhe"
					>
						<template v-slot:item.dtlog="{ item }">{{ formatarData(item.dtlog) }}</template>
						<template v-slot:item.tipo="{ item }">
							<v-chip x-small :color="corTipo(item.tipo)" text-color="white">{{ rotuloTipo(item.tipo) }}</v-chip>
						</template>
						<template v-slot:item.statuscode="{ item }">
							<v-chip x-small :color="corStatus(item.statuscode, item.tipo)" text-color="white">{{ item.statuscode || "-" }}</v-chip>
						</template>
						<template v-slot:item.resposta="{ item }">
							<span class="texto-curto">{{ item.resposta }}</span>
						</template>
					</v-data-table>
					<div class="text-caption grey--text mt-2">
						Segredos (tokens, client_secret) são ocultados e o tipo "token" nunca é listado. Clique numa linha para ver o texto completo.
					</div>
				</v-card-text>
			</v-tab-item>

			<!-- Testes -->
			<v-tab-item>
				<v-card-text>
					<v-row dense align="center">
						<v-col cols="12" sm="4">
							<v-select v-model="filialTeste" :items="opcoesFiliais" label="Filial" outlined dense hide-details />
						</v-col>
					</v-row>
					<div class="mt-4">
						<v-btn small outlined color="primary" class="mr-2 mb-2" :disabled="!itemTeste" @click="testar('autenticacao')">Autenticação (token)</v-btn>
						<v-btn small outlined color="teal" class="mr-2 mb-2" :disabled="!itemTeste" @click="testar('cadeia')">Validar cadeia</v-btn>
						<v-btn small outlined color="indigo" class="mr-2 mb-2" :disabled="!itemTeste" @click="testar('webhook')">Consultar webhook (GET)</v-btn>
						<v-btn small outlined color="orange darken-2" class="mr-2 mb-2" :disabled="!itemTeste || !itemTeste.flagsimulacao" @click="testar('emissao')">Emissão simulada (rota atual)</v-btn>
						<v-btn small outlined color="deep-purple" class="mr-2 mb-2" :disabled="!itemTeste || !itemTeste.flagsimulacao" @click="testar('emissaoNova')">Emissão simulada (URL nova)</v-btn>
					</div>
					<div class="text-caption grey--text mt-2">
						O resultado aparece no diálogo da tela e fica registrado na aba Atividade. As emissões só funcionam com a filial em modo Simulação.
					</div>
				</v-card-text>
			</v-tab-item>
		</v-tabs-items>

		<v-dialog v-model="dialogDetalhe" max-width="800">
			<v-card v-if="detalhe">
				<v-card-title>
					{{ rotuloTipo(detalhe.tipo) }}
					<v-spacer />
					<v-btn icon @click="dialogDetalhe = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>
				<v-card-text>
					<div class="text-body-2 mb-2">
						{{ formatarData(detalhe.dtlog) }} | filial {{ detalhe.idempresa === null ? "-" : detalhe.idempresa }} |
						nosso número {{ detalhe.nossonumero || "-" }} | HTTP {{ detalhe.statuscode || "-" }}
					</div>
					<pre class="bloco-codigo">{{ formatarResposta(detalhe.resposta) }}</pre>
				</v-card-text>
			</v-card>
		</v-dialog>

		<v-dialog v-model="dialogBaixaFilial" width="480">
			<v-card class="pa-5" v-if="alvoBaixaFilial">
				<v-card-title class="text-h5"><span class="mx-auto">Ligar a baixa automática</span></v-card-title>
				<v-card-text class="text-h6 text-center">
					Os próximos pagamentos de boletos da filial {{ alvoBaixaFilial.rotulo }} serão baixados no CISS automaticamente, sem passar por um operador.
					<div class="red--text text-body-2 mt-2">Confirme que a conferência desta filial já foi feita.</div>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn color="green darken-3" dark elevation="0" class="px-8" @click="alterarBaixaFilial(alvoBaixaFilial, true)">Ligar</v-btn>
					<v-btn color="red darken-3" dark elevation="0" class="px-8" @click="dialogBaixaFilial = false">Cancelar</v-btn>
					<v-spacer />
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="dialogCadastrar" width="480">
			<v-card class="pa-5">
				<v-card-title class="text-h5"><span class="mx-auto">Cadastrar webhook no Itaú</span></v-card-title>
				<v-card-text class="text-h6 text-center">
					Isso cria o cadastro de verdade para a filial {{ filialCadastro }}, apontando para as URLs configuradas no servidor.
					<div class="red--text text-body-2 mt-2">Consulte antes para ver se já existe cadastro.</div>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn color="green darken-3" dark elevation="0" class="px-8" @click="cadastrar">Cadastrar</v-btn>
					<v-btn color="red darken-3" dark elevation="0" class="px-8" @click="dialogCadastrar = false">Cancelar</v-btn>
					<v-spacer />
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-snackbar v-model="snackbar.ativo" :color="snackbar.cor" timeout="6000">{{ snackbar.texto }}</v-snackbar>
	</v-card>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";

const ROTULOS = {
	webhook_evento: "Evento recebido",
	webhook_baixa: "Baixa automática",
	webhook_simulado: "Registrado (sem baixa)",
	webhook_informativo: "Informativo",
	webhook_recusado: "Evento recusado",
	webhook_erro: "Erro no webhook",
	webhook_cadastro: "Cadastro do webhook",
	webhook_config: "Configuração alterada",
	webhook_negado: "Acesso recusado",
	webhook_ip_novo: "IP novo do Itaú",
	webhook_alerta: "Alerta de segurança",
	boletos_pix: "Emissão (rota atual)",
	boletos_pix_nova_url: "Emissão (URL nova)",
	teste_autenticacao: "Teste de autenticação",
	teste_cadeia: "Teste de cadeia",
	exportar_credenciais: "Exportação de credenciais",
	exportar_certificado: "Exportação de certificado",
};

const GRUPOS = [
	{ texto: "Todos", valor: "todos", tipos: [] },
	{
		texto: "Webhook",
		valor: "webhook",
		tipos: ["webhook_evento", "webhook_simulado", "webhook_informativo", "webhook_baixa", "webhook_recusado", "webhook_erro", "webhook_cadastro", "webhook_config"],
	},
	{ texto: "Segurança", valor: "seguranca", tipos: ["webhook_negado", "webhook_ip_novo", "webhook_alerta"] },
	{ texto: "Emissão", valor: "emissao", tipos: ["boletos_pix", "boletos_pix_nova_url"] },
	{ texto: "Testes", valor: "testes", tipos: ["teste_autenticacao", "teste_cadeia"] },
	{ texto: "Auditoria de exportações", valor: "auditoria", tipos: ["exportar_credenciais", "exportar_certificado"] },
];

export default {
	name: "TiPainelItau",
	props: {
		filiais: { type: Array, default: () => [] },
	},
	data: () => ({
		aba: 0,
		status: null,
		config: { urlwebhook: "", urloauth: "", escopo: "", valorminimo: 0.1, baixaautomatica: false, alertaemails: "" },
		salvandoConfig: false,
		testandoEmail: false,
		carregandoStatus: false,
		versaoSwitch: 0,
		baixaFilial: { disponivel: true, lista: [], filiaisfora: [], contas: {} },
		carregandoBaixaFilial: false,
		salvandoFilial: null,
		dialogBaixaFilial: false,
		alvoBaixaFilial: null,
		filialCadastro: null,
		consultandoCadastro: false,
		cadastrando: false,
		resultadoCadastro: null,
		dialogCadastrar: false,
		grupos: GRUPOS,
		grupoLog: "webhook",
		filialLog: null,
		limiteLog: 100,
		log: [],
		carregandoLog: false,
		autoAtualizar: false,
		temporizador: null,
		dialogDetalhe: false,
		detalhe: null,
		filialTeste: null,
		snackbar: { ativo: false, cor: "success", texto: "" },
		headersLog: [
			{ text: "Data", value: "dtlog" },
			{ text: "Filial", value: "idempresa" },
			{ text: "Tipo", value: "tipo" },
			{ text: "Nosso número", value: "nossonumero" },
			{ text: "Status", value: "statuscode" },
			{ text: "Resposta", value: "resposta", sortable: false },
		],
	}),
	computed: {
		...mapState(["backendUrl"]),
		filiaisOrdenadas() {
			return [...this.filiais].sort((a, b) => a.idempresa - b.idempresa);
		},
		opcoesFiliais() {
			return this.filiaisOrdenadas.map((f) => ({ text: this.rotuloFilial(f), value: f.idempresa }));
		},
		opcoesFiliaisTodas() {
			return [{ text: "Todas", value: null }, ...this.opcoesFiliais];
		},
		itemTeste() {
			return this.filiais.find((f) => f.idempresa === this.filialTeste) || null;
		},
		linhasBaixaFilial() {
			const estado = {};
			(this.baixaFilial.lista || []).forEach((l) => {
				estado[l.idempresa] = l;
			});
			const fora = this.baixaFilial.filiaisfora || [];
			return this.filiaisOrdenadas.map((f) => {
				const l = estado[f.idempresa] || {};
				return {
					idempresa: f.idempresa,
					rotulo: this.rotuloFilial(f),
					ativa: !!l.ativa,
					dtalteracao: l.dtalteracao || null,
					idusuario: l.idusuario || null,
					ultimotoken: f.ultimotoken || null,
					conta: (this.baixaFilial.contas || {})[f.idempresa] || null,
					fora: fora.indexOf(f.idempresa) >= 0,
				};
			});
		},
		podeGerenciar() {
			return this.permissao("boleto_webhook_gerenciar");
		},
		podeCadastrar() {
			const s = this.status;
			return !!(s && s.tabeladisponivel && s.configdisponivel && s.urlwebhook && s.urloauth);
		},
	},
	methods: {
		carregarTudo() {
			this.carregarStatus();
			this.carregarLog();
			this.carregarBaixaFilial();
		},
		carregarBaixaFilial() {
			this.carregandoBaixaFilial = true;
			return axios
				.post(`${this.backendUrl}boleto/webhook/baixa-filial/listar`)
				.then((res) => {
					this.baixaFilial = {
						disponivel: !!res.data.disponivel,
						lista: res.data.lista || [],
						filiaisfora: res.data.filiaisfora || [],
						contas: res.data.contas || {},
					};
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível carregar a baixa por filial.", "error"))
				.finally(() => {
					this.carregandoBaixaFilial = false;
				});
		},
		pedirAlterarBaixaFilial(filial, ligar) {
			if (ligar) {
				this.alvoBaixaFilial = filial;
				this.dialogBaixaFilial = true;
				// o switch já mudou na tela; recria o componente para voltar ao estado real até o usuário confirmar
				this.versaoSwitch++;
				return;
			}
			this.alterarBaixaFilial(filial, false);
		},
		alterarBaixaFilial(filial, ativa) {
			this.dialogBaixaFilial = false;
			this.salvandoFilial = filial.idempresa;
			return axios
				.post(`${this.backendUrl}boleto/webhook/baixa-filial/salvar`, { idempresa: filial.idempresa, ativa })
				.then(() => {
					this.avisar(`Baixa automática da filial ${filial.rotulo || filial.idempresa} ${ativa ? "ligada" : "desligada"}.`, "success");
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível alterar a baixa da filial.", "error"))
				.finally(() => {
					this.salvandoFilial = null;
					this.versaoSwitch++;
					this.carregarBaixaFilial();
				});
		},
		carregarStatus() {
			this.carregandoStatus = true;
			return axios
				.post(`${this.backendUrl}boleto/webhook/status`)
				.then((res) => {
					this.status = { ...res.data, contagem7dias: res.data.contagem7dias || [] };
					this.config = {
						urlwebhook: res.data.urlwebhook || "",
						urloauth: res.data.urloauth || "",
						escopo: res.data.escopo || "",
						valorminimo: res.data.valorminimo || 0.1,
						baixaautomatica: !!res.data.baixaautomatica,
						alertaemails: res.data.alertaemails || "",
					};
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível carregar o status do webhook.", "error"))
				.finally(() => {
					this.carregandoStatus = false;
				});
		},
		carregarLog() {
			this.carregandoLog = true;
			const grupo = GRUPOS.find((g) => g.valor === this.grupoLog) || GRUPOS[0];
			return axios
				.post(`${this.backendUrl}boleto/log/listar`, {
					tipos: grupo.tipos,
					idempresa: this.filialLog,
					limite: this.limiteLog,
				})
				.then((res) => {
					this.log = res.data.lista || [];
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível carregar a atividade.", "error"))
				.finally(() => {
					this.carregandoLog = false;
				});
		},
		configurarAutoAtualizar() {
			this.pararTemporizador();
			if (this.autoAtualizar) {
				this.temporizador = setInterval(this.carregarLog, 15000);
			}
		},
		pararTemporizador() {
			if (this.temporizador) {
				clearInterval(this.temporizador);
				this.temporizador = null;
			}
		},
		salvarConfig() {
			this.salvandoConfig = true;
			return axios
				.post(`${this.backendUrl}boleto/webhook/config/salvar`, this.config)
				.then(() => {
					this.avisar("Configuração do webhook salva.", "success");
					return this.carregarStatus();
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível salvar a configuração.", "error"))
				.finally(() => {
					this.salvandoConfig = false;
				});
		},
		enviarEmailTeste() {
			this.testandoEmail = true;
			return axios
				.post(`${this.backendUrl}boleto/webhook/alerta/testar`)
				.then((res) => {
					const r = res.data || {};
					if (r.erros && r.erros.length) {
						this.avisar(`${r.enviados} de ${(r.destinos || []).length} enviado(s). Falhas: ${r.erros.join("; ")}`, "warning");
					} else {
						this.avisar(`E-mail de teste enviado para ${(r.destinos || []).join(", ")}. Confira a caixa de entrada.`, "success");
					}
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível enviar o e-mail de teste.", "error"))
				.finally(() => {
					this.testandoEmail = false;
				});
		},
		consultarCadastro() {
			this.consultandoCadastro = true;
			this.resultadoCadastro = null;
			return axios
				.post(`${this.backendUrl}boleto/webhook/testar`, { idempresa: this.filialCadastro })
				.then((res) => {
					this.resultadoCadastro = res.data;
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível consultar o cadastro.", "error"))
				.finally(() => {
					this.consultandoCadastro = false;
				});
		},
		cadastrar() {
			this.dialogCadastrar = false;
			this.cadastrando = true;
			return axios
				.post(`${this.backendUrl}boleto/webhook/cadastrar`, { idempresa: this.filialCadastro })
				.then(() => {
					this.avisar("Cadastro enviado ao Itaú. Consulte para conferir e veja o log na aba Atividade.", "success");
					this.consultarCadastro();
					this.carregarStatus();
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "O Itaú recusou o cadastro.", "error"))
				.finally(() => {
					this.cadastrando = false;
				});
		},
		testar(acao) {
			if (this.itemTeste) {
				this.$emit("testar", { acao, item: this.itemTeste });
			}
		},
		abrirDetalhe(item) {
			this.detalhe = item;
			this.dialogDetalhe = true;
		},
		avisar(texto, cor) {
			this.snackbar = { ativo: true, cor, texto };
		},
		// "1-Ivaipora Matriz": código e nome da filial; sem nome, só o código
		rotuloFilial(f) {
			return f.nome ? `${f.idempresa}-${f.nome}` : `Filial ${f.idempresa}`;
		},
		rotuloTipo(tipo) {
			return ROTULOS[tipo] || tipo;
		},
		corTipo(tipo) {
			if (tipo === "webhook_baixa") return "green";
			if (tipo === "webhook_simulado") return "teal";
			if (tipo === "webhook_alerta") return "red";
			if (tipo === "webhook_negado") return "orange";
			if (tipo === "webhook_ip_novo") return "blue";
			if (tipo === "webhook_recusado") return "orange";
			if (tipo === "webhook_erro") return "red";
			if (tipo.indexOf("exportar_") === 0) return "red darken-2";
			if (tipo.indexOf("teste_") === 0) return "teal";
			return "blue-grey";
		},
		corStatus(codigo, tipo) {
			if (codigo >= 200 && codigo < 300) return "green";
			if (!codigo) return tipo === "webhook_recusado" ? "orange" : "grey";
			if (codigo === 404) return "orange";
			return "red";
		},
		explicarCadastro(codigo) {
			if (codigo >= 200 && codigo < 300) return "Cadastro encontrado.";
			if (codigo === 204 || codigo === 404) return "Sem cadastro para esse beneficiário, ou beneficiário não encontrado.";
			if (codigo === 403) return "Acesso negado a esse recurso. Confira se a filial e o beneficiário correspondem.";
			if (codigo === 401) return "Não autorizado: token ou certificado recusado.";
			return "Resposta inesperada do Itaú.";
		},
		formatarResposta(texto) {
			if (!texto) return "(sem corpo)";
			try {
				return JSON.stringify(JSON.parse(texto), null, 2);
			} catch (e) {
				return texto;
			}
		},
		formatarData(iso) {
			if (!iso) return "-";
			return new Date(iso).toLocaleString("pt-BR");
		},
	},
	created() {
		this.carregarTudo();
	},
	beforeDestroy() {
		this.pararTemporizador();
	},
};
</script>

<style scoped>
.bloco-codigo {
	background: #f5f5f5;
	border-radius: 4px;
	padding: 12px;
	font-size: 12px;
	white-space: pre-wrap;
	word-break: break-word;
	max-height: 360px;
	overflow: auto;
}
.texto-curto {
	display: inline-block;
	max-width: 420px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	vertical-align: bottom;
}
</style>
